/**
 * The status pill, for a state that is not a contest's (CAE-1556).
 *
 * `StatusPill` is keyed on `StatusPillState` — the five contest lifecycle
 * states plus `finished` — and `theme/tokens.ts` works hard to keep anything
 * else out of that union so a stray value cannot reach the lifecycle mapper or
 * the milestone rail. But the admin is full of two- and three-state things that
 * are not contests: an admin user is Active or Inactive, a configuration key is
 * Public or Admin only, a prize award is pending, completed or errored.
 *
 * Before this, each of those screens drew its own pill out of
 * `alpha(theme.palette.success.main, 0.12)` and friends — four near-identical
 * implementations, none of which the design had a say in, and all of which
 * landed a shade away from the pill beside them in the same table. This is
 * that pill, said once: the same geometry as `StatusPill` (both read
 * `statusPillSizes`) over palettes keyed by what the state *means*.
 *
 * Four of the five tones are the lifecycle palettes under semantic names — the
 * token layer hands `success` and `live` the same object — so "Active" is the
 * green "Live" is, rather than a second green that happens to be close.
 *
 * **It takes its label, where `StatusPill` derives one.** A contest's states
 * are a closed vocabulary that `contests/lifecycle.ts` owns and names; a tone
 * is not a word, and only the caller knows whether `neutral` here reads
 * "Inactive", "Admin only" or "Not set".
 */
import Box from '@mui/material/Box';
import type { Theme } from '@mui/material/styles';
import type { StatusPillSize } from '../../theme';

export type StatePillTone = 'neutral' | 'success' | 'danger' | 'info' | 'warn';

interface AdminStatePillProps {
  tone: StatePillTone;
  /** What the state is called on this screen. */
  label: string;
  /**
   * Defaults to `sm`, unlike `StatusPill`.
   *
   * Every caller sits in a dense table row, which is what `sm` is for, and
   * `StatusPill`'s `md` default exists for a token-naming reason that does not
   * apply here — nothing published `--state-pill-*` variables this would have
   * to agree with.
   */
  size?: StatusPillSize;
  /**
   * The screens this replaces each had a `data-testid` on the pill they drew
   * by hand, and their suites reach for it.
   */
  testId?: string;
}

export const AdminStatePill = ({ tone, label, size = 'sm', testId }: AdminStatePillProps) => (
  <Box
    component="span"
    data-testid={testId}
    sx={(theme: Theme) => {
      const palette = theme.tokens.componentColor.statePill[tone];
      const { padding, gap, labelSize, labelWeight, labelTracking } =
        theme.tokens.statusPillSizes[size];

      return {
        display: 'inline-flex',
        alignItems: 'center',
        gap: `${gap}px`,
        padding,
        borderRadius: `${theme.tokens.radius.pill}px`,
        border: `${theme.tokens.border.width} solid ${palette.border}`,
        backgroundColor: palette.bg,
        color: palette.text,
        fontFamily: theme.tokens.font.sans,
        fontSize: labelSize,
        fontWeight: labelWeight,
        letterSpacing: labelTracking,
        lineHeight: 1,
        whiteSpace: 'nowrap',
      };
    }}
  >
    {/*
      Decorative, as it is on `StatusPill`: the label beside it already says
      the state, and a second announcement of the same fact is noise. No
      pulse here — that belongs to a contest being `live`, which is a claim
      about entries arriving, not something an Active user account is doing.
    */}
    <Box
      aria-hidden
      component="span"
      sx={(theme: Theme) => ({
        width: `${theme.tokens.statusPillSizes[size].dotSize}px`,
        height: `${theme.tokens.statusPillSizes[size].dotSize}px`,
        flexShrink: 0,
        borderRadius: '50%',
        backgroundColor: theme.tokens.componentColor.statePill[tone].dot,
      })}
    />
    {label}
  </Box>
);
